import { useState, useEffect, useRef, useCallback } from 'react';
import { Image, Video, File, Check } from 'lucide-react';
import { MediaItem } from '../types/api';
import { apiBase } from '../services/api';

interface MediaGridProps {
  items: MediaItem[];
  onItemClick?: (item: MediaItem) => void;
  selectionMode?: boolean;
  onSelectionChange?: (selectedItems: MediaItem[]) => void;
  selectedItems?: MediaItem[];
}

/**
 * Media grid with viewport-driven thumbnail loading:
 * - only cards entering the viewport fetch their thumbnail
 * - a thumbnail response WITHOUT X-Thumb-Miss renders the type icon
 *   instead of pulling the full-size original (media uploaded before the
 *   .thumbs/ era has no thumbnail yet)
 * - object URLs are cached per key and revoked on unmount
 */
export function MediaGrid({
  items,
  onItemClick,
  selectionMode = false,
  onSelectionChange,
  selectedItems = [],
}: MediaGridProps) {
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [noThumb, setNoThumb] = useState<Set<string>>(new Set());
  const [thumbnailUrls, setThumbnailUrls] = useState<Map<string, string>>(new Map());
  const [internalSelected, setInternalSelected] = useState<MediaItem[]>([]);
  const sentinelRefs = useRef<Map<string, HTMLElement>>(new Map());
  const requested = useRef<Set<string>>(new Set());
  const blobUrls = useRef<Set<string>>(new Set());

  const current = selectedItems.length > 0 ? selectedItems : internalSelected;
  const isSelected = (item: MediaItem) => current.some(s => s.key === item.key);

  const toggle = (item: MediaItem) => {
    const next = isSelected(item)
      ? current.filter(s => s.key !== item.key)
      : [...current, item];
    if (selectedItems.length > 0) {
      onSelectionChange?.(next);
    } else {
      setInternalSelected(next);
      onSelectionChange?.(next);
    }
  };

  const toggleAll = () => {
    const next = current.length === items.length ? [] : [...items];
    if (selectedItems.length > 0) {
      onSelectionChange?.(next);
    } else {
      setInternalSelected(next);
      onSelectionChange?.(next);
    }
  };

  const loadThumb = useCallback(async (key: string) => {
    if (requested.current.has(key)) return;
    requested.current.add(key);
    try {
      const res = await fetch(
        `${apiBase()}/api/thumbnail/${encodeURIComponent(key)}`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error(String(res.status));
      // no real thumbnail stored yet — do not decode the full-size body
      if (res.headers.get('X-Thumb-Miss') === '1') {
        await res.body?.cancel();
        setNoThumb(prev => new Set(prev).add(key));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      blobUrls.current.add(url);
      setThumbnailUrls(prev => new Map(prev).set(key, url));
    } catch {
      setImageErrors(prev => new Set(prev).add(itemTypeForKey(key) === 'video' ? key : key));
    }
  }, []);

  const itemTypeForKey = (key: string): MediaItem['type'] => {
    const item = items.find(i => i.key === key);
    return item?.type ?? 'document';
  };

  // observe cards entering the viewport
  useEffect(() => {
    if (!items.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            const key = (e.target as HTMLElement).dataset.key;
            if (key) loadThumb(key);
          }
        }
      },
      { rootMargin: '300px 0px' },
    );
    for (const el of sentinelRefs.current.values()) io.observe(el);
    return () => io.disconnect();
  }, [items, loadThumb]);

  // reset per folder navigation
  useEffect(() => {
    requested.current.clear();
    sentinelRefs.current.clear();
    setImageErrors(new Set());
    setNoThumb(new Set());
  }, [items]);

  // revoke object URLs on unmount
  useEffect(() => {
    const urls = blobUrls.current;
    return () => { urls.forEach(u => URL.revokeObjectURL(u)); };
  }, []);

  const getIcon = (type: MediaItem['type']) => {
    if (type === 'image') return <Image className="h-7 w-7 text-gray-300" />;
    if (type === 'video') return <Video className="h-7 w-7 text-gray-300" />;
    return <File className="h-7 w-7 text-gray-300" />;
  };

  const renderItem = (item: MediaItem) => {
    const hasError = imageErrors.has(item.key);
    const missing = noThumb.has(item.key);
    const thumbUrl = thumbnailUrls.get(item.key);
    const selected = isSelected(item);
    const wantsThumb = item.type === 'image' || item.type === 'video';
    const loading = wantsThumb && !thumbUrl && !hasError && !missing;

    return (
      <div
        key={item.key}
        data-key={item.key}
        ref={(el) => { if (el) sentinelRefs.current.set(item.key, el); }}
        className={`media-item group relative ${selected ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
        onClick={() => selectionMode ? toggle(item) : onItemClick?.(item)}
      >
        {/* Thumbnail */}
        <div className="w-full h-full flex items-center justify-center bg-gray-100">
          {wantsThumb && thumbUrl && !hasError ? (
            <div className="relative w-full h-full">
              <img
                src={thumbUrl}
                alt={item.name}
                className="w-full h-full object-cover"
                onError={() => setImageErrors(prev => new Set(prev).add(item.key))}
              />
              {item.type === 'video' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center">
                    <Video className="h-4 w-4 text-white ml-0.5" />
                  </div>
                </div>
              )}
            </div>
          ) : loading ? (
            <div className="w-5 h-5 rounded-full border-2 border-gray-300 border-t-gray-500 animate-spin" />
          ) : (
            getIcon(item.type)
          )}
        </div>

        {/* Hover: subtle dim + filename */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-150" />
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-1.5 sm:p-2 translate-y-1 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-150">
          <p className="text-white text-[10px] sm:text-xs font-medium truncate leading-tight">{item.name}</p>
        </div>

        {/* Selection checkbox */}
        {selectionMode && (
          <div className="absolute top-1.5 left-1.5 z-10">
            <div
              className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                selected
                  ? 'bg-blue-500 border-blue-500 shadow-sm'
                  : 'bg-white/80 border-white/80'
              }`}
              onClick={e => { e.stopPropagation(); toggle(item); }}
            >
              {selected && <Check className="w-3 h-3 text-white" />}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!items.length) return (
    <div className="text-center py-12">
      <File className="h-10 w-10 text-gray-200 mx-auto mb-3" />
      <p className="text-gray-400 text-sm">No media files here</p>
    </div>
  );

  return (
    <div>
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <p className="section-label">
          Media{items.length > 0 ? ` · ${items.length}` : ''}
        </p>
        {selectionMode && (
          <button
            onClick={toggleAll}
            className="flex items-center gap-1.5 text-xs text-blue-600 font-medium hover:text-blue-700 transition-colors"
          >
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
              current.length === items.length
                ? 'bg-blue-500 border-blue-500'
                : 'border-gray-300'
            }`}>
              {current.length === items.length && <Check className="w-2.5 h-2.5 text-white" />}
            </div>
            {current.length === items.length ? 'Deselect all' : 'Select all'}
          </button>
        )}
      </div>

      <div className="media-grid">
        {items.map(renderItem)}
      </div>
    </div>
  );
}
